import { useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../config/firebaseConfig";
import {
  enregistrerFraisMiseEnPlace,
  fraisMiseEnPlaceId,
  watchFraisMiseEnPlace,
  watchTauxFraisMiseEnPlace,
} from "../oilmed.service";
import { fmt, showToast } from "../utils";

function fmtCDF(n) {
  return `${Math.round(n || 0).toLocaleString("fr-FR").replace(/[  ]/g, " ")} CDF`;
}

function parseMontant(value) {
  const n = Number(String(value || "").replace(/[\s  .]/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

const MAX_PREUVE_BYTES = 20 * 1024 * 1024;
const preuveFn = () =>
  httpsCallable(functions, "fraisMiseEnPlacePreuve", { timeout: 60000 });

function lireBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.readAsDataURL(file);
  });
}

// Ouvre la preuve PDF dans un nouvel onglet (ouvert pendant le clic pour ne
// pas être bloqué par le navigateur, puis rempli quand le PDF arrive)
async function voirPreuve(id) {
  const onglet = window.open("", "_blank");
  if (onglet) {
    onglet.document.body.innerHTML =
      '<p style="font-family:sans-serif;padding:24px;color:#555">⏳ Chargement de la preuve…</p>';
  }
  try {
    const { data } = await preuveFn()({ action: "get", id });
    const binary = window.atob(data.pdfBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    if (onglet && !onglet.closed) onglet.location.href = url;
    else window.location.assign(url);
    setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
  } catch (err) {
    if (onglet && !onglet.closed) onglet.close();
    showToast(err.message || "Ouverture de la preuve impossible", "error");
  }
}

const isoDay = (d) => (d ? d.toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));

/**
 * Section « Frais Mise En Place » de l'onglet Commande : montant (CDF) payé
 * pour la livraison de chaque programme du mois (terminal + produit actifs).
 * Boîte repliable, fermée par défaut.
 */
export default function FraisMiseEnPlace({ terminal, produit, commandes }) {
  const [open, setOpen] = useState(false);
  const [frais, setFrais] = useState({}); // numProg → frais déclarés
  const [edition, setEdition] = useState(null); // programme en cours de saisie
  const [montant, setMontant] = useState("");
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [fichier, setFichier] = useState(null); // preuve PDF choisie
  const [saving, setSaving] = useState(false);
  const [taux, setTaux] = useState({}); // CDF/m³ par terminal (onglet Paramètres)
  const [calcul, setCalcul] = useState(null); // détail du montant pré-rempli
  const fileRef = useRef(null);

  useEffect(
    () => watchFraisMiseEnPlace(terminal, produit, setFrais),
    [terminal, produit],
  );
  useEffect(() => watchTauxFraisMiseEnPlace(setTaux), []);

  // Programmes du mois, à partir des commandes (une commande = un BL)
  const programmes = Object.values(
    commandes.reduce((acc, c) => {
      if (!c.numProg) return acc;
      const p = (acc[c.numProg] ??= {
        numProg: String(c.numProg),
        date: c.date?.toDate?.() ?? c.createdAt?.toDate?.() ?? null,
        nbBL: 0,
        qty: 0,
        stations: new Set(),
      });
      p.nbBL += 1;
      p.qty += Number(c.qtyCom || 0);
      if (c.station) p.stations.add(c.station);
      return acc;
    }, {}),
  ).sort((a, b) => (b.date?.getTime() ?? 0) - (a.date?.getTime() ?? 0));

  const aDeclarer = programmes.filter((p) => !frais[p.numProg]).length;
  const total = programmes.reduce((a, p) => a + (frais[p.numProg]?.montantCDF || 0), 0);

  function ouvrirSaisie(p) {
    const f = frais[p.numProg];
    setEdition(p);
    // Nouveau programme : montant pré-rempli = taux du terminal × volume commandé
    const t = Number(taux[terminal]);
    if (!f && t > 0 && p.qty > 0) {
      setMontant(String(Math.round(t * p.qty)));
      setCalcul({ qty: p.qty, taux: t });
    } else {
      setMontant(f ? String(f.montantCDF) : "");
      setCalcul(null);
    }
    setDate(f?.date || isoDay(p.date));
    setNote(f?.note || "");
    setFichier(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  function choisirFichier(e) {
    const file = e.target.files?.[0] || null;
    if (file && file.type !== "application/pdf" && !/\.pdf$/i.test(file.name)) {
      showToast("La preuve doit être un fichier PDF", "error");
      e.target.value = "";
      return;
    }
    if (file && file.size > MAX_PREUVE_BYTES) {
      showToast("Fichier trop volumineux (max 20 Mo)", "error");
      e.target.value = "";
      return;
    }
    setFichier(file);
  }

  async function enregistrer() {
    const m = parseMontant(montant);
    if (!(m > 0)) return showToast("Montant invalide", "error");
    setSaving(true);
    try {
      await enregistrerFraisMiseEnPlace({
        terminalId: terminal,
        produit,
        numProg: edition.numProg,
        montantCDF: m,
        date,
        note,
      });
      if (fichier) {
        await preuveFn()({
          action: "upload",
          id: fraisMiseEnPlaceId(terminal, produit, edition.numProg),
          fileBase64: await lireBase64(fichier),
          fileName: fichier.name,
        });
      }
      showToast(
        `Frais du programme ${edition.numProg} enregistrés${fichier ? " avec la preuve" : ""} ✔`,
      );
      setEdition(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div
        className="card-header"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
      >
        <span className="card-title">
          <span style={{ display: "inline-block", width: 14, color: "var(--muted)" }}>
            {open ? "▾" : "▸"}
          </span>
          💰 Frais Mise En Place
        </span>
        {aDeclarer > 0 ? (
          <span className="badge badge-gold">{aDeclarer} à déclarer</span>
        ) : (
          <span className="badge badge-blue">{fmtCDF(total)}</span>
        )}
      </div>

      {open && (
        <div>
          {programmes.length === 0 ? (
            <div style={{ padding: 14, fontSize: 12, color: "var(--muted)" }}>
              Aucun programme ce mois-ci pour {terminal}.
            </div>
          ) : (
            programmes.map((p) => {
              const f = frais[p.numProg];
              return (
                <div
                  key={p.numProg}
                  role="button"
                  tabIndex={0}
                  onClick={() => ouvrirSaisie(p)}
                  onKeyDown={(e) => e.key === "Enter" && ouvrirSaisie(p)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderBottom: "1px solid var(--border)",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text)" }}>
                      Programme {p.numProg}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                      {p.date?.toLocaleDateString("fr-FR") ?? "—"} · {p.nbBL} BL ·{" "}
                      {fmt(p.qty)} m³ · {[...p.stations].join(", ")}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {f ? (
                      <>
                        <div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13, color: "var(--green)" }}>
                          {fmtCDF(f.montantCDF)}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--muted)" }}>
                          payé le {f.date.split("-").reverse().join("/")}
                          {f.preuve ? " · 📎" : " · sans preuve"} ✏
                        </div>
                      </>
                    ) : (
                      <span className="badge badge-gold">＋ Déclarer</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {programmes.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "10px 14px",
                fontSize: 12,
                fontFamily: "var(--font-mono)",
              }}
            >
              <span style={{ color: "var(--muted)" }}>Total du mois</span>
              <strong>{fmtCDF(total)}</strong>
            </div>
          )}
        </div>
      )}

      {/* ── Saisie des frais d'un programme ── */}
      <div
        className={`modal-overlay ${edition ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setEdition(null)}
      >
        <div className="modal">
          <div className="modal-handle" />
          <div className="modal-title">Frais Mise En Place</div>
          <div className="modal-sub">
            {"// "}PROGRAMME {edition?.numProg} — {terminal} ·{" "}
            {produit === "essence" ? "SUPER" : "GO"}
          </div>

          {edition && (
            <div className="info-strip">
              <span>{edition.nbBL} BL · {fmt(edition.qty)} m³ commandés</span>
              <span>{[...edition.stations].join(", ")}</span>
            </div>
          )}

          <div className="form-group">
            <label>Montant payé (CDF)</label>
            <input
              type="text"
              inputMode="numeric"
              value={montant}
              placeholder="Ex: 1500000"
              onChange={(e) => setMontant(e.target.value)}
            />
            {calcul && (
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3 }}>
                Pré-rempli : {fmt(calcul.qty)} m³ × {fmtCDF(calcul.taux)}/m³ (taux {terminal}) —
                modifiable
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Date de paiement</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="form-group">
            <label>Note (optionnel)</label>
            <input
              type="text"
              value={note}
              placeholder="Ex: Payé au transporteur X"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Preuve de paiement (PDF)</label>
            {edition && frais[edition.numProg]?.preuve && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 12,
                  marginBottom: 6,
                }}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  📎 {frais[edition.numProg].preuve.fileName}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => voirPreuve(frais[edition.numProg].id)}
                >
                  Voir
                </button>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              onChange={choisirFichier}
            />
            {edition && frais[edition.numProg]?.preuve && (
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 3 }}>
                Choisir un fichier remplace la preuve actuelle.
              </div>
            )}
          </div>

          <div className="btn-row">
            <button
              className="btn btn-green"
              style={{ flex: 1 }}
              disabled={saving || !(parseMontant(montant) > 0)}
              onClick={enregistrer}
            >
              {saving ? "En cours…" : "✔ Enregistrer"}
            </button>
            <button className="btn btn-ghost" onClick={() => setEdition(null)}>
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
